import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useApp } from "@/context/AppContext";
import { api } from "@/lib/api";
import { BookingCalendar } from "@/components/BookingCalendar";
import { Plane, Hotel, Landmark, Ticket, Loader2, ShieldCheck, Star } from "lucide-react";

const typeIcon = { flight: Plane, hotel: Hotel, package: Landmark };

export default function Dashboard() {
  const { t, money, user, authReady } = useApp();
  const nav = useNavigate();
  const [bookings, setBookings] = useState(null);

  useEffect(() => {
    if (authReady && !user) { nav("/auth"); return; }
    if (user) api.get("/bookings").then((r) => setBookings(r.data)).catch(() => setBookings([]));
  }, [user, authReady, nav]);

  return (
    <div className="bg-sand min-h-screen">
      <div className="bg-tigris py-12 px-4 sm:px-6">
        <div className="max-w-6xl mx-auto flex flex-wrap items-center justify-between gap-4">
          <div>
            <h1 className="text-white text-3xl font-bold font-display">{t.dashboard.title}</h1>
            {user && <p className="text-white/80 mt-1">{user.name} · {user.email}</p>}
          </div>
          {(user?.role === "admin" || user?.role === "staff") && (
            <Link to="/admin" data-testid="go-admin-btn" className="btn-gold inline-flex items-center gap-2 px-5 py-2.5 rounded-xl font-bold text-sm">
              <ShieldCheck size={16} /> {t.admin.title}
            </Link>
          )}
        </div>
      </div>
      <div className="max-w-6xl mx-auto px-4 sm:px-6 py-10">
        {bookings === null ? (
          <div className="flex justify-center py-20"><Loader2 className="animate-spin text-tigris" size={40} /></div>
        ) : (
          <div className="grid lg:grid-cols-[1fr_360px] gap-8 items-start">
            <div>
              {bookings.length === 0 ? (
                <div className="text-center py-20 bg-white rounded-2xl border border-slate-100">
                  <Ticket size={48} className="text-slate-300 mx-auto mb-4" />
                  <p className="text-slate-400 mb-5">{t.dashboard.empty}</p>
                  <Link to="/packages" className="btn-primary inline-flex px-6 py-3 rounded-xl font-bold">{t.common.viewAll}</Link>
                </div>
              ) : (
                <div className="space-y-4">
                  {bookings.map((b) => {
                    const Icon = typeIcon[b.type] || Ticket;
                    const confirmed = b.status === "confirmed";
                    return (
                      <div key={b.ref} data-testid={`booking-row-${b.ref}`} className="bg-white rounded-2xl p-5 border border-slate-100 shadow-sm flex flex-col sm:flex-row items-start sm:items-center gap-4">
                        <div className="w-12 h-12 rounded-xl bg-tigris/10 text-tigris flex items-center justify-center shrink-0"><Icon size={22} /></div>
                        <div className="flex-1">
                          <div className="font-bold text-slate-800">{b.title}</div>
                          <div className="text-xs text-slate-400 mt-0.5">{t.dashboard.ref}: <span className="font-mono font-semibold text-tigris">{b.ref}</span>{b.start_date && <> · {b.start_date}</>}</div>
                        </div>
                        <div className="text-lg font-extrabold text-tigris">{money(b.amount)}</div>
                        <span className={`text-xs font-bold px-3 py-1.5 rounded-full ${confirmed ? "bg-green-100 text-green-700" : b.status === "cancelled" ? "bg-red-100 text-red-700" : "bg-amber-100 text-amber-700"}`}>
                          {confirmed ? t.dashboard.confirmed : b.status === "cancelled" ? t.admin.cancelled : t.dashboard.pending}
                        </span>
                        <Link to={`/booking/${b.ref}`} data-testid={`view-voucher-${b.ref}`} className="btn-primary px-4 py-2 rounded-xl text-sm font-bold">{t.dashboard.viewVoucher}</Link>
                        {confirmed && b.type === "package" && (
                          <Link to={`/packages/${b.item_id}#reviews`} data-testid={`review-link-${b.ref}`} className="inline-flex items-center gap-1 text-xs font-bold text-gold hover:underline"><Star size={14} className="fill-gold" /> {t.reviews.rateTrip}</Link>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
            <div>
              <h2 className="text-base md:text-lg font-bold text-slate-700 mb-3">{t.dashboard.calendar}</h2>
              <BookingCalendar bookings={bookings} />
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
